"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createTask,
  updateTask,
  type LifeAllocation,
  type ScheduleBlock,
  type TaskItem,
} from "@/lib/api";
import type { TodayQueuePeekItem } from "@/lib/api/projection-types";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { parseUTCishDate } from "@/lib/utils";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";
import {
  LIFE_AREA_KEYS,
  LIFE_AREA_META,
  UNLINKED_EMOJI,
  type LifeAreaKey,
} from "@/lib/lifeAreas";

function formatBlockTimingChip(block: ScheduleBlock): string {
  const start = parseUTCishDate(block.start_time);
  const end = parseUTCishDate(block.end_time);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const todayKey = new Date().toDateString();
  const startKey = start.toDateString();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = tomorrow.toDateString();
  const fmtTime = (d: Date) =>
    d
      .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      .replace(":00", "");
  const range = `${fmtTime(start)}–${fmtTime(end)}`;
  if (startKey === todayKey) return range;
  if (startKey === tomorrowKey) return `Tomorrow ${fmtTime(start)}`;
  const day = start.toLocaleDateString([], { weekday: "short" });
  return `${day} ${fmtTime(start)}`;
}

interface Props {
  items: TodayQueuePeekItem[];
  fullQueueItems: TaskItem[];
  isExpanded: boolean;
  isLoading: boolean;
  onToggleExpanded: () => void;
  onTaskCreated?: () => void;
  onPromoteToFocus?: (task: TaskItem) => void;
  highlightedItemId?: string | null;
  goalCategoryById: Record<string, string | null>;
  goalCategoryHints: Array<{ title: string; category: string | null }>;
  lifeAllocation?: LifeAllocation | null;
  // True when a Start Here / restart focus card is rendered above
  // this section. The queue dedupes that task out (TodayScreen
  // filters it from listTasks), so when remainingCount drops to 0
  // we'd otherwise show "Nothing to do right now" right next to
  // a card that clearly DOES have a task — that's the fresh-Today
  // contradiction. With this flag we suppress the misleading copy.
  restartActive?: boolean;
  // Schedule blocks for any day, used to surface the time chip on
  // queue rows whose task is linked to a placed block. Without this
  // the user commits Place-on-Plan from capture and Today shows the
  // task as a plain todo, with no visible trace of the time slot
  // they just locked in. Tapping the chip reveals the block on Plan.
  scheduleBlocks?: ScheduleBlock[];
  onRevealBlock?: (block: ScheduleBlock) => void;
}

// Pull rail/text Tailwind classes from the canonical registry so this
// chip row stays in lockstep with PetalLifeWheel, BalanceList, etc.
const LIFE_AREA_COLORS: Record<string, { rail: string; text: string }> = Object.fromEntries(
  LIFE_AREA_KEYS.map((k) => [k, { rail: LIFE_AREA_META[k].rail, text: LIFE_AREA_META[k].text }]),
);

// Fallback emoji per life area for when the backend's LifeAllocation
// lookup doesn't include emoji metadata.
const LIFE_AREA_EMOJI: Record<string, string> = {
  ...Object.fromEntries(LIFE_AREA_KEYS.map((k) => [k, LIFE_AREA_META[k].emoji])),
  other: UNLINKED_EMOJI,
};

// "other" is rendered alongside the named areas so the user can put
// a task into a generic bucket without inventing a category. It maps
// to the unlinked tag on the backend (no specific life_area filter).
const ADD_TASK_AREA_KEYS = [...LIFE_AREA_KEYS, "other"] as const satisfies ReadonlyArray<
  LifeAreaKey | "other"
>;

const UNLINKED_COLORS = { rail: "bg-muted", text: "text-muted" };

function areaColors(key: string) {
  if (key === "other") return UNLINKED_COLORS;
  return LIFE_AREA_COLORS[key] ?? UNLINKED_COLORS;
}

const KNOWN_LIFE_AREAS = new Set(Object.keys(LIFE_AREA_COLORS));

function queueAreaKey(task: TaskItem, goalCategoryById: Record<string, string | null>): string {
  // 1. Check goal linkage
  const goalCategory = task.goal_id ? goalCategoryById[task.goal_id] : null;
  if (goalCategory && KNOWN_LIFE_AREAS.has(goalCategory)) return goalCategory;
  // 2. Check tags for life area
  if (task.tags) {
    for (const tag of task.tags) {
      const normalized = tag.toLowerCase().trim();
      if (KNOWN_LIFE_AREAS.has(normalized)) return normalized;
    }
  }
  // 3. Check category field if present
  const taskAny = task as unknown as Record<string, unknown>;
  if (taskAny.category && typeof taskAny.category === "string") {
    const cat = taskAny.category.toLowerCase().trim();
    if (KNOWN_LIFE_AREAS.has(cat)) return cat;
  }
  return "unlinked";
}

function areaLabel(key: string, lifeAllocation?: LifeAllocation | null): { title: string; emoji: string } {
  if (key === "other") return { title: "Other", emoji: LIFE_AREA_EMOJI.other };
  const area = lifeAllocation?.areas.find((item) => item.id === key || item.area === key);
  if (area) {
    return {
      title: area.label,
      emoji: area.emoji || LIFE_AREA_EMOJI[key] || "",
    };
  }
  if (key === "unlinked") return { title: "Other tasks", emoji: LIFE_AREA_EMOJI.other };
  return {
    title: key.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()),
    emoji: LIFE_AREA_EMOJI[key] ?? "",
  };
}

type AreaStack = {
  key: string;
  title: string;
  emoji: string;
  items: TaskItem[];
  totalMinutes: number;
};

function buildStacks(
  tasks: TaskItem[],
  goalCategoryById: Record<string, string | null>,
  lifeAllocation?: LifeAllocation | null
): AreaStack[] {
  const buckets = new Map<string, TaskItem[]>();
  for (const task of tasks) {
    const key = queueAreaKey(task, goalCategoryById);
    const bucket = buckets.get(key) ?? [];
    bucket.push(task);
    buckets.set(key, bucket);
  }

  const areaOrder = lifeAllocation?.areas.map((a) => a.id) ?? [];
  const allKeys = new Set([...areaOrder, ...Array.from(buckets.keys())]);

  return Array.from(allKeys)
    .filter((key) => (buckets.get(key)?.length ?? 0) > 0)
    .map((key) => {
      const meta = areaLabel(key, lifeAllocation);
      const items = buckets.get(key) ?? [];
      return {
        key,
        title: meta.title,
        emoji: meta.emoji,
        items,
        totalMinutes: items.reduce((sum, t) => sum + (t.estimated_minutes ?? 0), 0),
      };
    });
}

function estimateLabel(minutes: number): string {
  if (minutes <= 0) return "";
  if (minutes < 60) return `${minutes}m`;
  return `${(Math.round(minutes / 6) / 10)}h`;
}

function TodoCheckbox({
  task,
  onComplete,
}: {
  task: TaskItem;
  onComplete: (id: string) => void;
}) {
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(task.status === "completed");

  const handleClick = useCallback(async () => {
    if (done || completing) return;
    setCompleting(true);
    try {
      await updateTask(task.id, { status: "completed" });
      setDone(true);
      onComplete(task.id);
    } catch {
      setCompleting(false);
    }
  }, [task.id, done, completing, onComplete]);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handleClick(); }}
      disabled={completing}
      className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-colors ${
        done
          ? "border-accent bg-accent"
          : completing
            ? "border-accent/50 bg-accent/20"
            : "border-border bg-white hover:border-forest/40"
      }`}
      aria-label={done ? "Completed" : "Mark as done"}
    >
      {(done || completing) && (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
          <path d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

export default function QueuePeek({
  items,
  fullQueueItems,
  isExpanded,
  isLoading,
  onToggleExpanded,
  onTaskCreated,
  onPromoteToFocus,
  highlightedItemId = null,
  goalCategoryById,
  lifeAllocation = null,
  restartActive = false,
  scheduleBlocks = [],
  onRevealBlock,
}: Props) {
  const blockByTaskId = useMemo(() => {
    const map = new Map<string, ScheduleBlock>();
    for (const b of scheduleBlocks) {
      if (b.task_id && !b.is_completed) {
        const existing = map.get(b.task_id);
        if (!existing) {
          map.set(b.task_id, b);
        } else if (
          parseUTCishDate(b.start_time).getTime() <
          parseUTCishDate(existing.start_time).getTime()
        ) {
          map.set(b.task_id, b);
        }
      }
    }
    return map;
  }, [scheduleBlocks]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const activeTasks = fullQueueItems.filter((t) => t.status !== "completed" && !completedIds.has(t.id));
  const stacks = buildStacks(activeTasks, goalCategoryById, lifeAllocation);
  const remainingCount = activeTasks.length;
  const totalMinutes = activeTasks.reduce((sum, t) => sum + (t.estimated_minutes ?? 0), 0);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskMinutes, setNewTaskMinutes] = useState("");
  const [newTaskLifeArea, setNewTaskLifeArea] = useState<string | null>(null);
  const [newTaskSaving, setNewTaskSaving] = useState(false);
  const [saveAsShortcut, setSaveAsShortcut] = useState(false);
  // Per-life-area saved shortcuts. Persisted in localStorage so a user
  // can build up "Morning run" / "Pay bills" style quick-adds across
  // sessions without a backend change. Shape: { [areaKey]: string[] }.
  // The "__none" bucket holds shortcuts created without a life area.
  const [shortcuts, setShortcuts] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("today_task_shortcuts_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") setShortcuts(parsed);
      }
    } catch {
      /* ignore corrupt storage */
    }
  }, []);

  const persistShortcuts = useCallback((next: Record<string, string[]>) => {
    setShortcuts(next);
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("today_task_shortcuts_v1", JSON.stringify(next));
    } catch {
      /* storage may be unavailable in private mode */
    }
  }, []);

  // Shortcuts visible in the modal: prefer the area the user is
  // currently filtering by; fall back to "__none" so a fresh user with
  // no area selected still sees their general saved-tasks list.
  const visibleShortcutKey = newTaskLifeArea ?? "__none";
  const visibleShortcuts = shortcuts[visibleShortcutKey] ?? [];

  const removeShortcut = useCallback(
    (area: string, value: string) => {
      const list = shortcuts[area] ?? [];
      const next = { ...shortcuts, [area]: list.filter((entry) => entry !== value) };
      persistShortcuts(next);
    },
    [shortcuts, persistShortcuts],
  );

  const handleComplete = useCallback((id: string) => {
    setCompletedIds((prev) => new Set(prev).add(id));
  }, []);

  // Direct-skip for queue rows. The previous flow was: click row →
  // promote to focus → use the focus-card "Skip today" button.
  // Three steps to dismiss a task you don't want to do is too much,
  // and a freshly-added shortcut task wasn't always becoming focus
  // anyway, which made the existing skip button feel broken. The
  // hover × on each row gives a one-click skip in place.
  const [skippingId, setSkippingId] = useState<string | null>(null);
  const handleSkipQueueItem = useCallback(
    async (id: string) => {
      if (skippingId) return;
      setSkippingId(id);
      try {
        await updateTask(id, { status: "skipped" });
        onTaskCreated?.();
      } catch {
        /* leave the row visible — the user can retry */
      } finally {
        setSkippingId(null);
      }
    },
    [skippingId, onTaskCreated],
  );

  const handleAddTask = useCallback(async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    setNewTaskSaving(true);
    try {
      await createTask({
        title,
        estimated_minutes: newTaskMinutes ? parseInt(newTaskMinutes, 10) : undefined,
        due_date: new Date().toISOString().slice(0, 10),
        tags: newTaskLifeArea ? [newTaskLifeArea] : undefined,
      });
      // If "Save as shortcut" is on, store the title under the chosen
      // life area (or the __none bucket) so it appears next time.
      if (saveAsShortcut) {
        const bucket = newTaskLifeArea ?? "__none";
        const existing = shortcuts[bucket] ?? [];
        if (!existing.includes(title)) {
          persistShortcuts({ ...shortcuts, [bucket]: [...existing, title] });
        }
      }
      setNewTaskTitle("");
      setNewTaskMinutes("");
      setNewTaskLifeArea(null);
      setSaveAsShortcut(false);
      setShowAddTask(false);
      onTaskCreated?.();
    } catch {
      /* keep form open */
    } finally {
      setNewTaskSaving(false);
    }
  }, [newTaskTitle, newTaskMinutes, newTaskLifeArea, saveAsShortcut, shortcuts, persistShortcuts, onTaskCreated]);

  // Tracks the shortcut title currently being created so the pill can
  // flash a checkmark and the user gets clear "the click registered"
  // feedback even on fast networks.
  const [pendingShortcut, setPendingShortcut] = useState<string | null>(null);
  const [justAddedShortcut, setJustAddedShortcut] = useState<string | null>(null);

  // One-tap shortcut: create the task immediately with no extra prompts.
  // Inherits the area from the shortcut bucket it was stored under.
  // Closes the modal after the request completes so the user sees the
  // new task land in the queue underneath — keeping the modal open
  // hid the change and made the shortcut feel like a no-op.
  const handleQuickAdd = useCallback(
    async (title: string, area: string | null) => {
      if (newTaskSaving || pendingShortcut) return;
      setPendingShortcut(title);
      setNewTaskSaving(true);
      try {
        await createTask({
          title,
          due_date: new Date().toISOString().slice(0, 10),
          tags: area ? [area] : undefined,
        });
        setJustAddedShortcut(title);
        onTaskCreated?.();
        // Brief beat so the user perceives the success flash, then close.
        setTimeout(() => {
          setJustAddedShortcut(null);
          setShowAddTask(false);
        }, 320);
      } catch {
        /* swallow — user can retry */
      } finally {
        setPendingShortcut(null);
        setNewTaskSaving(false);
      }
    },
    [newTaskSaving, pendingShortcut, onTaskCreated],
  );

  // Peek: build from fullQueueItems if available (has tags for area color), else fall back to projection items
  const peekItems: Array<{ id: string; title: string; areaKey: string }> =
    fullQueueItems.length > 0
      ? fullQueueItems.slice(0, 3).map((t) => ({
          id: t.id,
          title: t.title,
          areaKey: queueAreaKey(t, goalCategoryById),
        }))
      : items.slice(0, 3).map((item) => ({
          id: item.id,
          title: item.title,
          areaKey: "unlinked",
        }));
  const chatRevealTodayKey = useMemo(
    () => [...activeTasks.map((task) => task.id), ...peekItems.map((item) => item.id)].join("|"),
    [activeTasks, peekItems],
  );
  useChatRevealHighlight("today", chatRevealTodayKey);

  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm" data-testid="queue-peek">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
            Today&apos;s tasks
          </p>
          <p className="mt-0.5 text-xs text-text-light">
            {remainingCount > 0
              ? `${remainingCount} remaining${totalMinutes > 0 ? ` · ${estimateLabel(totalMinutes)}` : ""}`
              : restartActive
                ? "Just the focus above for now."
                : "Nothing to do right now."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAddTask(true)}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-white text-muted transition-colors hover:border-forest/25 hover:text-forest"
            aria-label="Add task"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {(isLoading || remainingCount > 0 || fullQueueItems.length > 0) && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-medium text-text-light transition-colors hover:border-forest/25 hover:text-text"
            >
              {isExpanded ? "Collapse" : "Expand"}
            </button>
          )}
        </div>
      </div>

      {/* Expanded: grouped by life area with checkboxes */}
      {isExpanded ? (
        <div data-testid="today-full-queue" className="pb-2">
          {isLoading && fullQueueItems.length === 0 ? (
            <div className="space-y-2 px-4 pb-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-9 animate-pulse rounded-lg bg-secondary/50" />
              ))}
            </div>
          ) : stacks.length > 0 ? (
            <div className="space-y-3 px-3">
              {stacks.map((stack) => {
                const colors = areaColors(stack.key);
                const activeItems = stack.items.filter((t) => !completedIds.has(t.id));
                const doneItems = stack.items.filter((t) => completedIds.has(t.id));
                return (
                  <div key={stack.key} data-testid={`today-life-area-section-${stack.key}`}>
                    {/* Area header */}
                    <div className="flex items-center gap-2 px-1 pb-1">
                      <span className={`h-2.5 w-2.5 rounded-sm ${colors.rail}`} />
                      <span className={`text-[11px] font-semibold ${colors.text}`}>
                        {stack.emoji ? `${stack.emoji} ` : ""}{stack.title}
                      </span>
                      <span className="text-[10px] text-muted">
                        {activeItems.length}{stack.totalMinutes > 0 ? ` · ${estimateLabel(stack.totalMinutes)}` : ""}
                      </span>
                    </div>

                    {/* Active tasks */}
                    {activeItems.map((item) => {
                      const isSkipping = skippingId === item.id;
                      const linkedBlock = blockByTaskId.get(item.id) ?? null;
                      const blockTimingChip = linkedBlock
                        ? formatBlockTimingChip(linkedBlock)
                        : "";
                      return (
                        <div
                          key={item.id}
                          data-testid="today-full-queue-item"
                          data-reveal-id={item.id}
                          className={`group flex items-start gap-2.5 rounded-lg px-1 py-1.5 transition-colors ${
                            item.id === highlightedItemId
                              ? "animate-land-pulse bg-sage-pale/40"
                              : "hover:bg-bg/60"
                          } ${isSkipping ? "opacity-50" : ""}`}
                        >
                          <button
                            type="button"
                            onClick={() => onPromoteToFocus?.(item)}
                            className="flex min-w-0 flex-1 items-start gap-2.5 cursor-pointer text-left"
                          >
                            <span className="mt-1 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-forest/20 text-forest/40">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                              </svg>
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] leading-snug text-text">
                                {sanitizeSurfaceTitle(item.title)}
                              </p>
                            </div>
                          </button>
                          {/* Time chip — visible only when the task is
                              linked to a placed schedule block. Tap to
                              reveal the block on Plan (where edits
                              happen). Beats hiding the timing entirely
                              and forcing the user to switch surfaces
                              just to verify what they scheduled. */}
                          {linkedBlock && blockTimingChip && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRevealBlock?.(linkedBlock);
                              }}
                              data-testid="today-queue-block-time-chip"
                              className="mt-0.5 shrink-0 rounded-full border border-blue/30 bg-blue/8 px-2 py-0.5 text-[10px] font-medium text-blue transition-colors hover:bg-blue/15"
                              title="Open on Plan"
                            >
                              {blockTimingChip}
                            </button>
                          )}
                          {!linkedBlock && item.estimated_minutes ? (
                            <span className="mt-0.5 shrink-0 text-[10px] text-muted">{item.estimated_minutes}m</span>
                          ) : null}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleSkipQueueItem(item.id);
                            }}
                            disabled={isSkipping}
                            aria-label={`Skip ${item.title}`}
                            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted opacity-0 transition-opacity hover:bg-secondary hover:text-rose group-hover:opacity-100 disabled:opacity-50"
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}

                    {/* Completed tasks (struck through) */}
                    {doneItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-2.5 rounded-lg px-1 py-1.5 opacity-50"
                      >
                        <TodoCheckbox task={item} onComplete={handleComplete} />
                        <p className="min-w-0 flex-1 text-[13px] leading-snug text-muted line-through">
                          {sanitizeSurfaceTitle(item.title)}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : peekItems.length > 0 ? (
        /* Collapsed: show top 3 with life area color dots */
        <div className="px-4 pb-3 space-y-1" data-testid="today-queue-peek-list">
          {peekItems.map((item) => {
            const colors = areaColors(item.areaKey);
            return (
              <div
                key={item.id}
                data-testid="today-queue-peek-item"
                data-reveal-id={item.id}
                className={`flex items-center gap-2 rounded-lg px-1 py-1 ${
                  item.id === highlightedItemId
                    ? "animate-land-pulse bg-sage-pale/40"
                    : ""
                }`}
              >
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${colors.rail}`} />
                <p className="min-w-0 flex-1 truncate text-[13px] text-text">
                  {sanitizeSurfaceTitle(item.title)}
                </p>
              </div>
            );
          })}
        </div>
      ) : null}
      {/* Add task popup */}
      {showAddTask && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px]"
          onClick={() => setShowAddTask(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3
                className="font-serif text-[22px] font-medium tracking-[-0.3px] text-text"
              >
                Add task
              </h3>
              <button
                onClick={() => setShowAddTask(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-secondary hover:text-text"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {visibleShortcuts.length > 0 && (
              <div className="mb-4" data-testid="add-task-shortcuts">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                  Saved shortcuts
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {visibleShortcuts.map((entry) => {
                    const isPending = pendingShortcut === entry;
                    const isJustAdded = justAddedShortcut === entry;
                    return (
                      <span
                        key={entry}
                        className={`group inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all duration-200 ${
                          isJustAdded
                            ? "border-forest bg-sage-pale text-forest scale-[1.04]"
                            : isPending
                              ? "border-forest/40 bg-sage-pale/60 text-forest"
                              : "border-border bg-bg text-text-light hover:border-forest/40 hover:bg-sage-pale/60 hover:text-forest"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => void handleQuickAdd(entry, newTaskLifeArea)}
                          className="flex items-center gap-1 disabled:cursor-not-allowed"
                          disabled={Boolean(pendingShortcut)}
                        >
                          {isJustAdded ? (
                            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                              <path d="M2.5 7.5 L6 11 L11.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : isPending ? (
                            <span className="inline-block h-[10px] w-[10px] animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
                          ) : (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polygon points="13 2 4 14 11 14 11 22 20 10 13 10 13 2" />
                            </svg>
                          )}
                          {entry}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeShortcut(visibleShortcutKey, entry)}
                          className="ml-0.5 text-muted opacity-0 transition-opacity group-hover:opacity-100 hover:text-rose"
                          aria-label={`Remove shortcut ${entry}`}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                What needs to be done?
              </p>
              <input
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void handleAddTask(); }}
                placeholder="e.g. Buy groceries"
                className="w-full border-0 border-b-2 border-border bg-transparent px-0 py-1.5 text-sm text-text placeholder-muted focus:border-forest focus:outline-none"
                autoFocus
              />
            </div>

            <div className="mb-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                Time
              </p>
              <input
                type="number"
                value={newTaskMinutes}
                onChange={(e) => setNewTaskMinutes(e.target.value)}
                placeholder="e.g. 20 min"
                min={1}
                max={480}
                className="w-full rounded-lg border border-border bg-cream px-3 py-1.5 text-sm text-text placeholder-muted focus:outline-none focus:ring-1 focus:ring-forest/30"
              />
            </div>

            <div className="mb-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                Life area
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ADD_TASK_AREA_KEYS.map((key) => {
                  const meta = areaLabel(key, lifeAllocation);
                  const colors = areaColors(key);
                  const active = newTaskLifeArea === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewTaskLifeArea(active ? null : key)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        active
                          ? `${colors.rail} text-white`
                          : "border border-border bg-white text-text-light hover:bg-bg"
                      }`}
                    >
                      <span aria-hidden>{meta.emoji}</span>
                      <span>{meta.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setSaveAsShortcut((current) => !current)}
              className="mt-3 flex w-full items-center gap-2 text-left text-[12px] text-text-light transition-colors hover:text-text"
              data-testid="add-task-save-shortcut"
              aria-pressed={saveAsShortcut}
            >
              <span
                className={`relative inline-block h-[18px] w-[34px] flex-shrink-0 rounded-full transition-colors ${
                  saveAsShortcut ? "bg-forest-med" : "bg-border"
                }`}
              >
                <span
                  className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-transform ${
                    saveAsShortcut ? "translate-x-[18px]" : "translate-x-[2px]"
                  }`}
                />
              </span>
              Save as a shortcut for next time
            </button>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => void handleAddTask()}
                disabled={!newTaskTitle.trim() || newTaskSaving}
                className="flex-1 rounded-full bg-forest-med py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-50"
              >
                {newTaskSaving ? "Adding..." : "Add"}
              </button>
              <button
                onClick={() => setShowAddTask(false)}
                className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
